// react
import { MouseEvent, useContext, useState } from 'react';

// types
import { ErrorCause, Feedback } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// mui
import { Grid } from '@mui/material';
import { LoadingButton } from '@mui/lab';

// fields
import { Input } from '../../fields/Input/Input';

// ui
import { AlertFeedback } from '../../ui/AlertFeedback/AlertFeedback';

// props
export interface FormAddTagProps {
	onClose?: () => void;
	onSuccess?: () => void;
}

export const FormAddTag = ({ onClose, onSuccess }: FormAddTagProps) => {
	// hooks
	const { t } = useTranslation();

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	// vars
	const idForm = 'form-add-tag';

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		const form = document.querySelector('#form-add-tag') as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());

		try {
			// clear feedback
			setFeedback(null);

			// set loader
			setIsLoading(true);

			const fetchAddTag = await fetch(`${process.env.REACT_APP_API_URL}/tags`, {
				method: 'POST',
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
				body: JSON.stringify({
					name: entries.name,
					project: project?.id,
				}),
			});
			const resAddTag = await fetchAddTag.json();

			if (resAddTag.error) {
				throw new Error(resAddTag.error.message, {
					cause: {
						details: resAddTag.error.details,
						id: resAddTag.error.id,
						message: resAddTag.error.message,
						res: resAddTag,
					},
				});
			}

			if (onSuccess) {
				onSuccess();
			} // set success banner
			setBanner({
				hasClose: true,
				message: t('alert.tagCreated', { name: entries.name }),
				severity: 'success',
			});

			// close dialog
			if (onClose) {
				onClose();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			const feedbackNew: Feedback = {
				alert: {
					children: t('error.default'),
				},
			};

			if (cause) {
				if (cause.id === 'TAG_UNAUTHORIZED_POST') {
					feedbackNew.alert.children = t('error.unauthorized');
				}

				// store full error response for debugging
				if (cause.res) {
					feedbackNew.response = cause.res;
				}
			}

			// set feedback
			setFeedback(feedbackNew);
		} finally {
			// set loader
			setIsLoading(false);
		}
	};

	return (
		<form id={idForm} name="formAddTag" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Input id={`${idForm}-name`} label={t('name')} name="name" required={true} />
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<AlertFeedback feedback={feedback} />
					</Grid>
				)}
				<Grid item xs={12}>
					<LoadingButton loading={isLoading} type="submit" variant="contained">
						{t('addTag')}
					</LoadingButton>
				</Grid>
			</Grid>
		</form>
	);
};
